import React from 'react';
import { trackEmailClick, trackGitHubClick, trackLinkedInClick, trackResumeClick } from '../utils/analytics';

interface TimelineEvent {
  type: 'education' | 'internship';
  title: string;
  institution: string;
  period: string;
  points: string[];
  tags: string[];
}

const timelineEvents: TimelineEvent[] = [
  {
    type: 'internship',
    title: 'Research Intern',
    institution: 'Samsung Research Institute, Noida (Next Level Labs, On-device AI Team)',
    period: 'May 2026 - Present',
    points: [
      'Optimizing Large Vision-Language Models (VLMs) and LLMs for on-device inference on Samsung mobile chipsets, focusing on memory-efficient attention layouts and quantization-aware compression.',
      'Conducting latency profiling on mobile NPUs/GPUs; identifying bottlenecks in token generation and attention modules.'
    ],
    tags: ['VLMs', 'LLMs', 'On-device AI', 'NPU/GPU Profiling', 'Model Compression']
  },
  {
    type: 'internship',
    title: 'Research Intern',
    institution: 'Soket AI',
    period: 'Jan 2026 - Feb 2026',
    points: [
      'Developed Agri-LLM, agricultural domain-specific language model targeted for Indian farming applications.',
      'Designed LLM-as-a-judge automated evaluation methodologies and generated supervised fine-tuning (SFT) datasets.'
    ],
    tags: ['Agri-LLM', 'LLM-as-a-judge', 'SFT Data Generation', 'NLP']
  },
  {
    type: 'education',
    title: 'Incoming M.Tech. Research',
    institution: 'Indian Institute of Science (IISc), Bangalore',
    period: 'Accepted via Early Admission',
    points: [
      'Accepted early admission for graduate research studies in Artificial Intelligence / Computing at the Indian Institute of Science.'
    ],
    tags: ['Graduate Research', 'IISc Bangalore', 'Early Admission']
  },
  {
    type: 'internship',
    title: 'Summer Research Intern',
    institution: 'CVIG Lab, IIT Gandhinagar',
    period: 'May 2025 - July 2025',
    points: [
      'Developed PanoTo3D, a single-panorama 3D scene reconstruction and novel view synthesis pipeline using 3D Gaussian Splatting.',
      'Structured depth-based point clouds; synthesized multi-view supervision via inpainting to stabilize geometry learning; trained 3D Gaussian Splatting models for sparse-view inverse rendering.'
    ],
    tags: ['3D Gaussian Splatting', 'PanoTo3D', 'Depth Estimation', 'Inpainting']
  },
  {
    type: 'internship',
    title: 'Undergraduate Researcher',
    institution: 'MUSE Lab, IIT Gandhinagar (Collaborator: UChicago Radiology)',
    period: 'Jan 2025 - April 2025',
    points: [
      'Developed DeepLabV3-based models with custom combined Dice-Focal loss to segment histotripsy ablation bubble clouds in clinical ultrasound imagery.',
      'Improved mean IoU to 83% (+7% over prior baseline) and accuracy to 97%.'
    ],
    tags: ['Ultrasound Segmentation', 'DeepLabV3', 'Dice-Focal Loss', 'PyTorch']
  },
  {
    type: 'education',
    title: 'B.Tech in Artificial Intelligence',
    institution: 'Indian Institute of Technology Gandhinagar, Gujarat, India',
    period: 'July 2023 - Present',
    points: [
      'CPI: 9.3 / 10.00 | Rank: 1st in Department.',
      'Core Member of the ML Club, IITGN.',
      'Academic Mentor for MA 103: Calculus and Linear Algebra (supporting 300+ students).'
    ],
    tags: ['B.Tech AI', 'Rank 1', 'ML Club', 'Teaching Assistant']
  }
];

const publications = [
  {
    title: 'Deep Learning-based Ultrasound Image Segmentation for Histotripsy Ablation Zone Delineation',
    authors: 'S. Gaonkar, H. Shekhar, K. Bader, et al.',
    venue: 'IEEE International Ultrasonics Symposium (IUS 2025), Rotterdam, Netherlands. (Accepted)'
  },
  {
    title: 'Improved Segmentation of Histotripsy Bubble Clouds Using Modern CNN Architectures and Combined Dice-Focal Loss',
    authors: 'S. Gaonkar, H. Shekhar, K. Bader, et al.',
    venue: 'Frontiers in Acoustics. (Submitted)'
  }
];

const achievements = [
  {
    title: 'Academic Honors',
    desc: 'Academic Excellence Scholarship (2024-25, 2025-26); Department Rank 1 (B.Tech. AI, IITGN).'
  },
  {
    title: 'National Rank',
    desc: 'JEE Advanced AIR 2825 (Top 1.5% of 190k); JEE Main AIR 1987 (Top 0.2% of 1M).'
  },
  {
    title: 'Competitions',
    desc: 'Top 150 teams out of 20,000+ in Amazon ML Challenge 2025; Runner-up in Robotics HackRush.'
  }
];

const skills = {
  languages: ['Python', 'C++', 'C', 'Verilog', 'JavaScript', 'Java', 'SQL'],
  frameworks: ['PyTorch', 'Hugging Face Transformers', 'OpenCV', 'Scikit-learn', 'Weights & Biases', 'SciPy'],
  tools: ['Git', 'Docker', 'Basys3 FPGA', 'Xilinx Vivado', 'VS Code', 'Linux', 'FastAPI', 'React']
};

const About: React.FC = () => {
  const handleDownloadPDF = () => {
    trackResumeClick('about_download_cv');
    window.open('/assets/SohamGaonkarResume.pdf', '_blank');
  };

  return (
    <section className="min-h-screen bg-zinc-105 dark:bg-slate-900 transition-colors duration-300 py-12 md:py-20 px-3 sm:px-4 md:px-6 font-sans text-black dark:text-white">
      <div className="max-w-5xl mx-auto flex flex-col gap-12 sm:gap-16">
        
        {/* Header Title */}
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-black dark:text-white mb-2" style={{ fontFamily: 'Inter, DM Sans, sans-serif' }}>
            About Me
          </h1>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 font-medium">
            Soham Gaonkar — AI Undergraduate, Researcher & Developer
          </p>
        </div>

        {/* Top Card: Bio & Details Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl overflow-hidden w-full mx-auto flex flex-col md:flex-row items-start md:items-stretch border border-zinc-200/50 dark:border-slate-700/60">
          
          {/* Left: Profile Photo, Socials, PDF Button */}
          <div className="flex flex-col items-center w-full md:w-[34%] py-8 md:py-12 px-6 bg-zinc-50 dark:bg-slate-700 border-b md:border-b-0 md:border-r border-zinc-200 dark:border-slate-600">
            <div className="relative mb-4">
              <span className="absolute inset-0 rounded-full ring-4 ring-blue-500/80 dark:ring-blue-400/80"></span>
              <img
                src="/assets/profile.jpeg"
                alt="Soham Gaonkar"
                className="w-28 h-28 rounded-full object-cover border-4 border-white dark:border-slate-700 shadow-lg relative z-10"
              />
            </div>
            
            <h2 className="text-xl sm:text-2xl font-extrabold text-black dark:text-white mb-1.5 mt-2 text-center" style={{ fontFamily: 'Inter, DM Sans, sans-serif' }}>
              Soham Gaonkar
            </h2>
            <p className="text-sm sm:text-base text-gray-500 dark:text-slate-350 text-center mb-6" style={{ fontFamily: 'Inter, DM Sans, sans-serif' }}>
              AI Undergraduate at IITGN
            </p>
            
            {/* Social Icons */}
            <div className="flex gap-4 mb-6">
              <a href="https://github.com/Soham-Gaonkar" target="_blank" rel="noopener noreferrer" aria-label="GitHub" onClick={() => trackGitHubClick('about_profile_card')} className="text-gray-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.477 2 2 6.484 2 12.021c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.021C22 6.484 17.523 2 12 2z" /></svg>
              </a>
              <a href="https://www.linkedin.com/in/soham-gaonkar-885426280/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" onClick={() => trackLinkedInClick('about_profile_card')} className="text-gray-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-9h3v9zm-1.5-10.28c-.966 0-1.75-.79-1.75-1.75s.784-1.75 1.75-1.75 1.75.79 1.75 1.75-.784 1.75-1.75 1.75zm13.5 10.28h-3v-4.5c0-1.08-.02-2.47-1.5-2.47-1.5 0-1.73 1.17-1.73 2.39v4.58h-3v-9h2.89v1.23h.04c.4-.75 1.38-1.54 2.84-1.54 3.04 0 3.6 2 3.6 4.59v4.72z" /></svg>
              </a>
              <a href="mailto:soham.gaonkar@iitgn.ac.in" className="text-gray-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" aria-label="Email" onClick={() => trackEmailClick('about_profile_card')}>
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M2.01 6.81A2.25 2.25 0 0 1 4.25 5.5h15.5c.83 0 1.6.5 1.99 1.31.13.26.21.54.25.83v10.06A2.25 2.25 0 0 1 19.75 20.5H4.25A2.25 2.25 0 0 1 2 18.7V7.64c.04-.29.12-.57.25-.83zm1.72.69l8.27 6.2c.2.15.47.15.67 0l8.27-6.2a.75.75 0 0 0-.47-.19H4.25a.75.75 0 0 0-.52.19zm16.77 1.62l-6.98 5.23a2.25 2.25 0 0 1-2.72 0l-6.98-5.23V18.7c0 .41.34.75.75.75h15.5c.41 0 .75-.34.75-.75V9.12z" /></svg>
              </a>
            </div>

            {/* Resume Button */}
            <button
              onClick={handleDownloadPDF}
              className="w-full text-center px-5 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm sm:text-base shadow transition-all duration-300 hover:scale-[1.02] active:scale-95"
              aria-label="Download CV PDF"
            >
              Download PDF CV
            </button>
          </div>

          {/* Right: Bio Text Paragraphs */}
          <div className="flex-1 w-full md:pl-10 flex flex-col justify-center py-8 md:py-12 px-6">
            <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-slate-200 mb-6 leading-relaxed" style={{ fontFamily: 'Inter, DM Sans, sans-serif' }}>
              Hi, I am Soham Gaonkar, a junior undergraduate in Artificial Intelligence at IIT Gandhinagar. I enjoy working at the intersection of mathematics, computing, and physics, and I like building things that are both technically strong and practically useful. My current focus is on Computer Vision, Model Compression, on-device AI, and Medical Image Computing.
            </p>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-slate-350 leading-relaxed" style={{ fontFamily: 'Inter, DM Sans, sans-serif' }}>
              Recently, I have been working as a Research Intern at Next Level Labs, Samsung R&D Institute (Noida) in the on-device AI team. Previously, I was a Research Intern at Soket AI and a Summer Research Intern at CVIG Lab (IIT Gandhinagar).
            </p>
          </div>

        </div>

        {/* Education & Internships Timeline */}
        <div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mb-8 text-center" style={{ fontFamily: 'Inter, DM Sans, sans-serif' }}>
            Education & Internships
          </h2>
          
          <div className="relative border-l-2 border-zinc-200 dark:border-slate-800 ml-4 sm:ml-6 pl-8 sm:pl-10 space-y-8">
            {timelineEvents.map((event, idx) => (
              <div key={idx} className="relative group">
                
                {/* Timeline Dot Node */}
                <span className={`absolute -left-[37px] sm:-left-[45px] top-3 w-4 h-4 rounded-full border-4 bg-white dark:bg-slate-900 transition-all duration-300 group-hover:scale-125 ${
                  event.type === 'education'
                    ? 'border-blue-500 group-hover:border-blue-600'
                    : 'border-blue-400 group-hover:border-blue-500'
                }`} />

                {/* Event Card */}
                <div className="bg-white dark:bg-slate-800 border border-zinc-200 dark:border-zinc-700/60 rounded-3xl p-6 sm:p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md border-l-4 border-l-blue-500">
                  
                  {/* Header Information */}
                  <div className="flex flex-wrap justify-between items-baseline mb-3 gap-2">
                    <span className="px-3 py-1 text-xs font-bold rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400">
                      {event.type === 'education' ? 'Education' : 'Internship'}
                    </span>
                    
                    <span className="text-xs sm:text-sm font-semibold text-gray-500 dark:text-slate-400">
                      {event.period}
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white leading-snug mb-1">
                    {event.title}
                  </h3>
                  
                  <div className="text-md font-semibold text-gray-700 dark:text-slate-300 mb-4">
                    {event.institution}
                  </div>

                  {/* Details Points */}
                  <ul className="list-disc pl-5 text-gray-600 dark:text-slate-350 text-sm sm:text-base leading-relaxed flex flex-col gap-2 mb-5">
                    {event.points.map((pt, pIdx) => (
                      <li key={pIdx}>{pt}</li>
                    ))}
                  </ul>

                  {/* Tech/Field Badges */}
                  <div className="flex flex-wrap gap-2">
                    {event.tags.map((tag) => (
                      <span 
                        key={tag}
                        className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-zinc-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300 border border-zinc-200/40 dark:border-zinc-700/40"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                </div>

              </div>
            ))}
          </div>
        </div>

        {/* Bottom Rows: Publications & Achievements (Left) + Technical Skills (Right) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
          
          {/* Left Column: Publications & Achievements */}
          <div className="flex flex-col gap-8 sm:gap-12">
            
            {/* Publications Block */}
            <div className="bg-white dark:bg-slate-800 border border-zinc-200 dark:border-zinc-700/60 rounded-3xl p-6 sm:p-8 shadow-sm">
              <h2 className="text-2xl font-bold mb-6 text-slate-900 dark:text-white" style={{ fontFamily: 'Inter, DM Sans, sans-serif' }}>
                Publications
              </h2>

              <div className="flex flex-col gap-6">
                {publications.map((pub, idx) => (
                  <div key={idx} className="relative pl-3 border-l-2 border-blue-500 dark:border-blue-500">
                    <h3 className="text-md sm:text-lg font-bold text-slate-900 dark:text-white leading-snug mb-1">
                      "{pub.title}"
                    </h3>
                    <div className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 mb-1">{pub.authors}</div>
                    <div className="text-xs sm:text-sm font-semibold text-blue-600 dark:text-blue-400 italic">{pub.venue}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Achievements Block */}
            <div className="bg-white dark:bg-slate-800 border border-zinc-200 dark:border-zinc-700/60 rounded-3xl p-6 sm:p-8 shadow-sm">
              <h2 className="text-2xl font-bold mb-6 text-slate-900 dark:text-white" style={{ fontFamily: 'Inter, DM Sans, sans-serif' }}>
                Achievements
              </h2>

              <div className="flex flex-col gap-5">
                {achievements.map((ach, idx) => (
                  <div key={idx} className="group p-4 bg-zinc-50 dark:bg-slate-900/50 rounded-2xl border border-zinc-100 dark:border-slate-800 hover:border-blue-500/50 transition-colors duration-300">
                    <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1 transition-colors">
                      {ach.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 leading-relaxed">
                      {ach.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Right Column: Technical Skills */}
          <div className="flex flex-col">
            <div className="bg-white dark:bg-slate-800 border border-zinc-200 dark:border-zinc-700/60 rounded-3xl p-6 sm:p-8 shadow-sm h-full">
              <h2 className="text-2xl font-bold mb-6 text-slate-900 dark:text-white" style={{ fontFamily: 'Inter, DM Sans, sans-serif' }}>
                Technical Skills
              </h2>
              
              <div className="flex flex-col gap-6">
                <div>
                  <h3 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2.5">Languages</h3>
                  <div className="flex flex-wrap gap-2">
                    {skills.languages.map((s) => (
                      <span key={s} className="px-3 py-1.5 bg-blue-50 dark:bg-slate-900/50 text-xs font-bold rounded-lg text-blue-700 dark:text-blue-300 border border-blue-100/50 dark:border-blue-900/40">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2.5">Libraries & Frameworks</h3>
                  <div className="flex flex-wrap gap-2">
                    {skills.frameworks.map((s) => (
                      <span key={s} className="px-3 py-1.5 bg-blue-50 dark:bg-slate-900/50 text-xs font-bold rounded-lg text-blue-700 dark:text-blue-300 border border-blue-100/50 dark:border-blue-900/40">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2.5">Systems & Tools</h3>
                  <div className="flex flex-wrap gap-2">
                    {skills.tools.map((s) => (
                      <span key={s} className="px-3 py-1.5 bg-blue-50 dark:bg-slate-900/50 text-xs font-bold rounded-lg text-blue-700 dark:text-blue-300 border border-blue-100/50 dark:border-blue-900/40">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default About;